import React from 'react';
import { Subtract } from 'utility-types';
import { browserInstance as browser } from '@/utils/browser';

import { TrackerProps } from '@/types/common';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { isBank } from '@/config/core';

import { TRACKING_OPERATION, TRACKING_PAGE, TRACKING_SECTION, TRACKING_TARGET } from './constants';
import { onErrorCount } from '../onError';
import { AppBITrack } from './tracker/AppBITrack';
import { ChatbotBITrack } from './tracker/ChatbotBITrack';
import reporter from '../report';

const isApp = browser.shopeeAppOrLiveChat;
const appTrack = new AppBITrack({ isReact: false, isPC: false });
const trackObj = isApp ? appTrack : null; // 非APP暂不上报埋点 webTrack;
export const chatbotTrackObj = new ChatbotBITrack();

/**
 * @param {{
 *  section?: TRACKING_SECTION;
 *  target?: TRACKING_TARGET;
 *  page?: TRACKING_PAGE;
 *  data?: {
 *    [propName: string]: any;
 *  };
 *  operation: TRACKING_OPERATION;
 *  usage_id?: number;
 * }} data
 */
export function dispatchTrack(data?: {
  operation: TRACKING_OPERATION;
  section?: TRACKING_SECTION;
  target?: TRACKING_TARGET;
  page?: TRACKING_PAGE;
  usage_id?: number;
  data?: any;
  reportdbOnly?: boolean;
}) {
  const { operation = TRACKING_OPERATION.VIEW, page = TRACKING_PAGE.MAIN_PAGE, reportdbOnly = false } = data || {};
  const info: any = {
    page_type: page,
    operation,
  };

  if (data?.section) {
    info.page_section = data.section;
  }

  if (data?.target) {
    info.target_type = data.target;
  }

  if (data?.data) {
    info.data = { ...data.data };

    const intent_list = [];
    if (data.data.viewed_objects) {
      // impression report
      data.data.viewed_objects.forEach((v: any) => {
        const intentId = v.intent_id ?? v.kb_intent_id;
        if (intentId) {
          intent_list.push(intentId);
        }
      });
    } else {
      // click report
      const intentId = data.data.intent_id ?? data.data.kb_intent_id;
      if (intentId) {
        intent_list.push(intentId);
      }
    }
    // 所有包含intent的上报都需要增加intent_list
    if (intent_list.length !== 0) {
      info.data.intent_list = intent_list;

      reporter.apiBatchReport({ type: 'count', value: { label: 'intent_list_report_monitor', extend_1: info.page_section, extend_2: info.target_type }});
    }
  }

  // performance埋点为1
  if (data?.usage_id) {
    info.usage_id = data.usage_id;
  }

  try {
    (!reportdbOnly || isBank()) && trackObj && trackObj.report(info);
    chatbotTrackObj && chatbotTrackObj.report(info);
  } catch (e) {
    // @ts-ignore
    onErrorCount(e);
  }
}

export const tracker = <P extends TrackerProps>(WrappedComponent: React.ComponentType<P>) => {
  return class extends React.Component<Subtract<P, TrackerProps>> {
    listRef: any[];
    constructor(props: any) {
      super(props);

      this.listRef = [];
    }

    componentDidMount() {
      if (this.listRef.length === 0) {
        return;
      }

      addObserver(this.listRef);
    }

    componentWillUnmount() {
      removeObserver(this.listRef);
    }

    passedRef = (ele: any, index: any) => {
      if (typeof index === 'number') {
        this.listRef[index] = ele;
        return;
      }

      this.listRef.push(ele);
    };

    click = ({
      operation = TRACKING_OPERATION.CLICK,
      trackingname,
      data,
      ...rest
    }: {
      operation: TRACKING_OPERATION;
      trackingname: Extract<TRACKING_SECTION, TRACKING_TARGET>;
      data: any;
    }) => {
      dispatchTrack({
        operation,
        section: TRACKING_SECTION[trackingname],
        target: TRACKING_TARGET[trackingname],
        data,
        ...rest,
      });
    };

    render() {
      return (
        <WrappedComponent
          {...(this.props as P)}
          passedRef={this.passedRef}
          tracker={{
            click: this.click,
            impression: {
              addObserver: (index: any) => {
                // 在需要手动调用时使用
                if (typeof index === 'number') {
                  // 有ref
                  if (this.listRef[index]) {
                    addObserver(this.listRef[index]);
                  }
                } else {
                  addObserver(this.listRef);
                }
              },
              removeObserver: () => {
                // 在需要手动调用时使用
                removeObserver(this.listRef);
              },
            },
          }}
        />
      );
    }
  };
};
